import React, { FC, useEffect, useRef, useState } from 'react'
import ReactToPrint from 'react-to-print';
import moment from 'moment';
import WayshipDate from 'components/_common/WayshipDate';
import { IVesselUser } from 'redux/app/feedback/@types';
import classNames from 'classnames';
import imgStar from '../../../../assets/images/certificate/star.png';
import imgBorder from '../../../../assets/images/certificate/border.png';
import imgLogo from '../../../../assets/images/certificate/volteo-logo.png';

interface ICertificateViewProps {
    user: IVesselUser;
    onClose: () => void;
    editVesselUserTrainingDate: (userId: string, date: string) => void;
    onUserChange: (user: IVesselUser) => void;
    isWayshipSupportAccount: boolean;
}

const CertificateView: FC<ICertificateViewProps> = (props) => {
    const { user, isWayshipSupportAccount } = props;
    const componentRef = useRef<any>();
    const [isTrainingDateEditable, setIsTrainingDateEditable] = React.useState(false);
    const [trainingDate, setTrainingDate] = useState('');
    const [error, setError] = useState({
        isValid: true,
        message: '',
    });
    useEffect(() => {
        setTrainingDate(user?.trainingDate || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.trainingDate]);
    return (
        <section
            className={classNames(
				'bg-white py-4 px-6 rounded-lg m-auto overflow-y-auto',
				{ 
					'h-auto w-full md:w-84': !(user?.trainingDate && !isTrainingDateEditable),
					'w-full md:w-[80vw] h-[80vh]': user?.trainingDate && !isTrainingDateEditable,
				 }
			)}
        >
			<div>
				<div className='flex items-center justify-between mb-5 gap-4'>
					<h2 className='m-0 text-xl font-bold'>
						{
							isTrainingDateEditable?
								'Edit Training Date'
								: user?.trainingDate?
									'Print Certificate'
									: 'Generate Certificate'
						}
					</h2>
					<div className='flex items-center gap-x-5'>
						{
							user?.trainingDate && !isTrainingDateEditable?
								<>
									<ReactToPrint
										trigger={() => (
											<button title='Print' className="p-4 py-3 flex items-center text-gray-700 bg-blue-100 rounded-md outline-none hover:bg-blue-200 focus:bg-blue-300">
												<span className="bp3-icon bp3-icon-print" />
											</button>
										)}
										content={() =>
											componentRef.current
										}
										pageStyle="@media print{
													@page {
														size: landscape;
														margin: 0;
													}}"
									/>
									{
										isWayshipSupportAccount?
											<button
												onClick={() => {
													setIsTrainingDateEditable(true);
												}}
												title='Change Training Date'
												className="p-4 py-3 flex items-center text-gray-700 bg-blue-100 rounded-md outline-none hover:bg-blue-200 focus:bg-blue-300 "
											>
												<span className="bp3-icon bp3-icon-edit" />
											</button>
											: null
									}
								</>
								: null
						}
						<button
							aria-label="Close"
							onClick={() => {
								if (isTrainingDateEditable) {
									setIsTrainingDateEditable(false);
								} else {
									props.onClose();
								}
							}}
							className="p-4 py-3 flex items-center text-gray-600 bg-gray-100 rounded-md outline-none hover:bg-gray-200 focus:bg-gray-300 "
						>
							<span className="bp3-icon bp3-icon-cross" />
						</button>
					</div>
				</div>
				<div className='overflow-auto'>
					<div className={classNames(
						{
							'w-full': !(user?.trainingDate && !isTrainingDateEditable),
							'w-fit lg:w-full': user?.trainingDate && !isTrainingDateEditable,
						}
					)}>
					{
						user?.trainingDate && !isTrainingDateEditable?
							<div ref={componentRef} className='flex items-center h-full justify-center'>
								<div className='bg-white w-[1022px] h-[722px] p-4 relative'>
									<img src={imgBorder} alt="border" className='absolute right-0 top-0 h-[90%] w-1/6' />
									<div className='border-4 font-montserrat border-solid border-[#cb9d60] text-[#384a5f] flex flex-col items-center px-[69px] pt-9 pb-[41px] h-full'>
										<img src={imgLogo} alt="logo" width={100} />
										<p className='mt-[13px] text-lg font-semibold leading-normal relative w-full text-center'>
											Volteo Maritime Pte Ltd (Singapore)
											<img src={imgStar} alt="border" width={100} height={100} className='absolute right-24 transform bottom-5 translate-y-1/2' />
										</p>
										<div className='mt-12 border-0 border-b-4 border-solid border-[#384a5f]'>
											<h2 className='m-0 font-bold text-4xl leading-[24px] tracking-[3.2px] pb-2'>
												CERTIFICATE OF KNOWLEDGE
											</h2>
										</div>
										<div className='mt-9 w-[752px] text-justify px-14 text-lg'>
											This is to certify that
											<span className='mx-1 font-semibold text-lg leading-6'>
												{user?.name.toUpperCase()}
											</span>
											has completed the Wayship v1.0.62 virtual training on
											<span className='mx-1 font-semibold text-lg leading-6'>
												{moment(user?.trainingDate).format('dddd, MMM DD YYYY')}
											</span>
											in compliance with STCW-95 Regulation I/14.1.4.
										</div>
										<div className='flex flex-col w-full items-end mt-64 mr-16'>
											<div>
												<p className='text-xl font-medium leading-normal text-blue-900'>Dinesh Taunk</p>
												<p className='text-3xl font-medium font-script leading-normal text-blue-900'>Dinesh Taunk</p>
												<p className='text-xl leading-normal font-medium text-gray-600'>Trainer</p>
											</div>
										</div>
									</div>
									<img src={imgBorder} alt="border" className='absolute left-0 bottom-0 h-[90%] w-1/6 rotate-180' />
								</div>
							</div>
							: isWayshipSupportAccount? (
								<div className='flex flex-col h-full mb-5'>
									<WayshipDate
										value={trainingDate || ''}
										title='Training Date'
										type='date'
										dataKey='trainingDate'
										error={error}
										onChangeHandler={(value) => {
											setError({
												isValid: true,
												message: '', 
											})
											setTrainingDate(value);
										}}
									/>
									<button
										className="box-border flex items-center max-w-min justify-center p-3 px-8 font-medium text-white rounded-lg outline-none focus:ring ws-button whitespace-nowrap"
										disabled={!trainingDate}
										onClick={() => {
											if (trainingDate) {
												if (isTrainingDateEditable) {
													setIsTrainingDateEditable(false);
												}
												props.editVesselUserTrainingDate(
													user._id,
													trainingDate
												);
												props.onUserChange({
													...user,
													trainingDate: trainingDate,
												})
											} else {
												setError({
													isValid: false,
													message: 'Please select training date', 
												})
											}
										}}
									>
										{
											isTrainingDateEditable?
												'Save'
												: 'Generate'
										}
									</button>
								</div>
							): <p>No Certificate Found</p>
					}
					</div>
				</div>
			</div>
        </section>
    )
}

export default CertificateView